import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Geolocation } from '@capacitor/geolocation';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { Network } from '@capacitor/network';
import { Share } from '@capacitor/share';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import OneSignal from '@onesignal/capacitor-plugin';

const state = {
  initialized: false,
  oneSignalReady: false,
  member: null,
  config: {},
};

const isNative = () => Capacitor.isNativePlatform();

function emit(name, detail = {}) {
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

function createOfflineBanner() {
  if (document.getElementById('ahad-native-network-banner')) return;
  const banner = document.createElement('div');
  banner.id = 'ahad-native-network-banner';
  banner.textContent = 'İnternet bağlantısı bekleniyor…';
  banner.setAttribute('role', 'status');
  Object.assign(banner.style, {
    position: 'fixed',
    top: 'max(8px, env(safe-area-inset-top))',
    left: '14px',
    right: '14px',
    zIndex: '2147483647',
    display: 'none',
    padding: '11px 16px',
    borderRadius: '14px',
    background: '#7f1d1d',
    color: '#fff',
    textAlign: 'center',
    font: '700 13px -apple-system,BlinkMacSystemFont,Segoe UI,sans-serif',
    boxShadow: '0 10px 28px rgba(127,29,29,.25)',
  });
  document.body.appendChild(banner);
}

function setNetworkStatus(connected) {
  const banner = document.getElementById('ahad-native-network-banner');
  if (banner) banner.style.display = connected ? 'none' : 'block';
  document.documentElement.classList.toggle('ahad-native-offline', !connected);
  emit('ahad-mobile-network', { connected });
}

async function configureNativeUi() {
  try {
    await StatusBar.setStyle({ style: Style.Light });
    if (Capacitor.getPlatform() === 'android') {
      await StatusBar.setBackgroundColor({ color: '#1C2B52' });
    }
  } catch (_) {}
  try { await SplashScreen.hide(); } catch (_) {}
}

async function configureNetwork() {
  createOfflineBanner();
  try {
    const current = await Network.getStatus();
    setNetworkStatus(Boolean(current.connected));
    await Network.addListener('networkStatusChange', status => {
      setNetworkStatus(Boolean(status.connected));
    });
  } catch (_) {}
}

async function configureDeepLinks() {
  try {
    await App.addListener('appUrlOpen', event => {
    const incoming = String(event.url || '');
    const base = String(state.config.baseUrl || '').replace(/\/+$/, '');
    if (base && incoming.startsWith(base)) {
      location.href = incoming;
    } else if (base && incoming.startsWith('ahadsen://open')) {
      const target = incoming.replace(/^ahadsen:\/\/open\/?/, '');
      location.href = `${base}/${target}`;
    }
    });
  } catch (_) {}

  if (Capacitor.getPlatform() === 'android') {
    try {
      await App.addListener('backButton', ({ canGoBack }) => {
        if (canGoBack || history.length > 1) {
          history.back();
        } else {
          App.minimizeApp();
        }
      });
    } catch (_) {}
  }
}

function configureExternalLinks() {
  document.addEventListener('click', event => {
    const anchor = event.target && event.target.closest ? event.target.closest('a[href]') : null;
    if (!anchor || !isNative()) return;

    let url;
    try { url = new URL(anchor.href, location.href); } catch (_) { return; }

    const allowedHost = String(state.config.allowedHost || location.hostname);
    const isWeb = url.protocol === 'http:' || url.protocol === 'https:';
    const isExternalHost = isWeb && url.hostname !== allowedHost && !url.hostname.endsWith('.' + allowedHost);
    const isSystemScheme = ['tel:', 'mailto:', 'sms:', 'whatsapp:'].includes(url.protocol);

    if (isExternalHost || isSystemScheme) {
      event.preventDefault();
      if (isWeb) {
        Browser.open({ url: url.href, presentationStyle: 'popover' }).catch(() => {
          location.href = url.href;
        });
      } else {
        location.href = url.href;
      }
    }
  }, true);
}

async function configureOneSignal() {
  const appId = String(state.config.oneSignalAppId || '').trim();
  if (!appId) {
    emit('ahad-mobile-push', { configured: false, permission: false });
    return;
  }

  try {
    OneSignal.setConsentRequired(true);
    OneSignal.initialize(appId);
    state.oneSignalReady = true;

    if (localStorage.getItem('ahad_push_consent') === '1') {
      OneSignal.setConsentGiven(true);
    }

    OneSignal.Notifications.addClickListener(event => {
      const launchUrl = event?.notification?.additionalData?.url
        || event?.notification?.launchURL
        || event?.notification?.additionalData?.link;
      if (launchUrl) location.href = String(launchUrl);
    });

    if (state.member) await identifyMember(state.member);
    emit('ahad-mobile-push', { configured: true });
  } catch (error) {
    emit('ahad-mobile-push', { configured: false, error: String(error?.message || error) });
  }
}

async function identifyMember(member) {
  state.member = member || null;
  if (!state.oneSignalReady || !state.member?.id) return false;

  try {
    OneSignal.login(String(state.member.id));
    OneSignal.User.addTags({
      member_id: String(state.member.id),
      member_no: String(state.member.memberNo || ''),
      union: String(state.member.union || 'AHAD-SEN'),
      role: 'member',
    });
    return true;
  } catch (_) {
    return false;
  }
}

async function initialize(config = {}) {
  state.config = { ...(window.AHAD_MOBILE_CONFIG || {}), ...config };
  if (!isNative()) return { native: false };
  if (state.initialized) return { native: true, platform: Capacitor.getPlatform() };

  state.initialized = true;
  document.documentElement.classList.add('ahad-native-app', `ahad-native-${Capacitor.getPlatform()}`);

  await Promise.all([
    configureNativeUi(),
    configureNetwork(),
    configureDeepLinks(),
  ]);
  configureExternalLinks();
  await configureOneSignal();

  emit('ahad-mobile-ready', { platform: Capacitor.getPlatform() });
  return { native: true, platform: Capacitor.getPlatform() };
}

async function requestPushPermission() {
  if (!isNative()) return { native: false, accepted: false };
  if (!state.oneSignalReady) {
    return { native: true, accepted: false, reason: 'not_configured' };
  }

  try {
    OneSignal.setConsentGiven(true);
    localStorage.setItem('ahad_push_consent', '1');
    const accepted = await OneSignal.Notifications.requestPermission(false);
    if (accepted && state.member) await identifyMember(state.member);
    try {
      await Haptics.notification({ type: accepted ? NotificationType.Success : NotificationType.Warning });
    } catch (_) {}
    emit('ahad-mobile-push', { configured: true, permission: Boolean(accepted) });
    return { native: true, accepted: Boolean(accepted) };
  } catch (error) {
    return { native: true, accepted: false, error: String(error?.message || error) };
  }
}

async function logoutMember() {
  state.member = null;
  if (!state.oneSignalReady) return;
  try { OneSignal.logout(); } catch (_) {}
}

async function getCurrentPosition() {
  if (!isNative()) {
    throw new Error('native_required');
  }
  await Geolocation.requestPermissions({ permissions: ['location'] });
  const result = await Geolocation.getCurrentPosition({
    enableHighAccuracy: true,
    timeout: 15000,
    maximumAge: 30000,
  });
  try { await Haptics.impact({ style: ImpactStyle.Light }); } catch (_) {}
  return {
    latitude: result.coords.latitude,
    longitude: result.coords.longitude,
    accuracy: result.coords.accuracy,
  };
}

async function share(options = {}) {
  if (!isNative()) return false;
  const result = await Share.share({
    title: String(options.title || document.title || 'AHAD-SEN'),
    text: String(options.text || ''),
    url: String(options.url || location.href),
    dialogTitle: 'Paylaş',
  });
  return Boolean(result);
}

async function haptic(kind = 'light') {
  if (!isNative()) return;
  const style = kind === 'heavy' ? ImpactStyle.Heavy : kind === 'medium' ? ImpactStyle.Medium : ImpactStyle.Light;
  try { await Haptics.impact({ style }); } catch (_) {}
}

window.AHADMobile = {
  initialize,
  isNative,
  loginMember: identifyMember,
  logoutMember,
  requestPushPermission,
  getCurrentPosition,
  share,
  haptic,
  openExternal: url => Browser.open({ url: String(url), presentationStyle: 'popover' }),
};

initialize(window.AHAD_MOBILE_CONFIG || {});
